import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import type { StatusTone } from '../../utils/run-status';

import { TONE_FILL, TONE_SOFT } from './tones';

interface StatusBadgeProps {
  label: string;
  tone: StatusTone;
  /** Plain-language meaning, shown on hover/focus. */
  explanation?: ReactNode;
  /** Pulse the dot — something is actively moving. */
  pulse?: boolean;
  /** Icon shown in place of the dot — e.g. a lock on a verdict held below its score. */
  icon?: LucideIcon;
  className?: string;
}

/**
 * The single badge for every status-like value in Content (run, batch,
 * resolution, grounding, severity…): dot + label, tone from `tones.ts`, and an
 * optional tooltip so each value can explain itself.
 */
export function StatusBadge({
  label,
  tone,
  explanation,
  pulse = false,
  icon: Icon,
  className,
}: StatusBadgeProps) {
  const badge = (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap',
        TONE_SOFT[tone],
        explanation !== undefined && 'cursor-help',
        className,
      )}
      {...(explanation !== undefined && { tabIndex: 0 })}
    >
      {Icon ? (
        <Icon aria-hidden="true" className="size-3 shrink-0" />
      ) : pulse ? (
        // Live: a ping halo behind the dot (still under reduced motion, so only the dot shows).
        <span aria-hidden="true" className="relative inline-flex size-1.5 shrink-0">
          <span
            className={cn(
              'absolute inline-flex size-full rounded-full opacity-70 motion-safe:animate-ping',
              TONE_FILL[tone],
            )}
          />
          <span className={cn('relative inline-flex size-full rounded-full', TONE_FILL[tone])} />
        </span>
      ) : (
        <span
          aria-hidden="true"
          className={cn('size-1.5 shrink-0 rounded-full', TONE_FILL[tone])}
        />
      )}
      {label}
    </span>
  );
  if (explanation === undefined) return badge;
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs leading-relaxed">
        {explanation}
        <TooltipPrimitive.Arrow className="fill-popover" />
      </TooltipContent>
    </Tooltip>
  );
}
